import { t } from 'ttag'
import React from 'react'
import { Lang, Stats } from '../../../../services/api.types'

import { ReactComponent as EmployeesIcon } from '../../../../assets/v3/icons/dashboard/employees.svg'
import { ReactComponent as EmployeesEngagementRateIcon } from '../../../../assets/v3/icons/dashboard/employees-engagement-rate.svg'
import { ReactComponent as UsersEngagementRateIcon } from '../../../../assets/v3/icons/dashboard/users-engagement-rate.svg'
import { ReactComponent as DailyStepsIcon } from '../../../../assets/v3/icons/dashboard/daily-steps.svg'
import { ReactComponent as TotalUsersIcon } from '../../../../assets/v3/icons/dashboard/total-users.svg'
import { ReactComponent as ActiveUsersIcon } from '../../../../assets/v3/icons/dashboard/active-users.svg'
import { ReactComponent as RegisteredUsersIcon } from '../../../../assets/v3/icons/dashboard/registered-users.svg'
import { ReactComponent as HeartIcon } from '../../../../assets/v3/icons/dashboard/heart.svg'
import { ReactComponent as HeartRateIcon } from '../../../../assets/v3/icons/dashboard/heart-rate.svg'
import { ReactComponent as MedianActivityIcon } from '../../../../assets/v3/icons/dashboard/median-activity.svg'
import { ReactComponent as ExemplaryActiveIcon } from '../../../../assets/v3/icons/dashboard/exemplary-active.svg'
import { ReactComponent as SectorIcon } from '../../../../assets/v3/icons/dashboard/sector.svg'
import { ReactComponent as MostActiveGroupIcon } from '../../../../assets/v3/icons/dashboard/most-active-group.svg'
import { ReactComponent as MostActiveDayIcon } from '../../../../assets/v3/icons/dashboard/most-active-day.svg'
import { ReactComponent as MostAdvancedIcon } from '../../../../assets/v3/icons/dashboard/most-advanced.svg'
import { ReactComponent as MostEngagedIcon } from '../../../../assets/v3/icons/dashboard/most-engaged.svg'
import { ReactComponent as MostActiveIcon } from '../../../../assets/v3/icons/dashboard/most-active.svg'
import { ReactComponent as TotalStepsIcon } from '../../../../assets/v3/icons/dashboard/total-steps.svg'
import { ReactComponent as TotalCaloriesIcon } from '../../../../assets/v3/icons/dashboard/total-calories.svg'
import { ReactComponent as ChallengesStartedIcon } from '../../../../assets/v3/icons/dashboard/challenges-started.svg'
import { ReactComponent as ChallengesEndedIcon } from '../../../../assets/v3/icons/dashboard/challenges-ended.svg'
import { ReactComponent as ChallengeParticipantsIcon } from '../../../../assets/v3/icons/dashboard/challenge-participants.svg'
import { ReactComponent as EditIcon } from '../../../../assets/v3/icons/dashboard/edit.svg'
import { ReactComponent as InfoIcon } from '../../../../assets/v3/icons/dashboard/info.svg'
import { formatThousands } from '../../../../common/helpers/translations'
import moment from 'moment'
import { IconButton, Tooltip } from '@material-ui/core'
import { primaryBlue, red } from '../../../../styles/material-ui-theme-v2'
import makeStyles from '@material-ui/core/styles/makeStyles'
import YumTooltip from '../../../../components/YumTooltip/YumTooltip'
import { currentLang } from '../../../../i18nInit'

export interface StackedStats {
  current: Stats
  previous: Stats
  current2?: Stats
  previous2?: Stats
}

export interface IStatCardProps {
  stats: StackedStats
  editEmployees?: () => void
}

export const getStatLabel = (key: string) => {
  switch (key) {
    case 'COMPANY_EMPLOYEES':
      return t`Employees`
    case 'COMPANY_TOTAL_USERS':
      return t`Total users`
    case 'COMPANY_ACTIVE_USERS':
      return t`Active users`
    case 'COMPANY_ACTIVE_USERS_FROM_TOTAL_PERCENTAGE':
      return t`User engagement rate`
    case 'COMPANY_TOTAL_USERS_FROM_EMPLOYEES_PERCENTAGE':
      return t`Employee engagement rate`
    /*case 'COMPANY_REGISTERED_USERS':
      return t`Registered users`*/
    case 'COMPANY_HR_USERS_PERCENTAGE':
      return t`Users with HR monitors`
    case 'COMPANY_MEDIAN_ACTIVITY':
      return t`Median activity`
    case 'COMPANY_EXEMPLARY_ACTIVE':
      return t`Exemplary active users`
    case 'COMPANY_MOST_ACTIVE_DAY':
      return t`Most active day`
    case 'COMPANY_STEPS_PER_USER_PER_DAY':
      return t`Average daily steps`
    case 'COMPANY_TOTAL_STEPS':
      return t`Total steps`
    case 'COMPANY_TOTAL_CALORIES':
      return t`Total calories`
    case 'COMPANY_TOTAL_MINUTES':
      return t`Total minutes`
    case 'COMPANY_CHALLENGES':
      return t`Challenges`
    /*case 'COMPANY_CHALLENGES_STARTED':
      return t`Challenges started`
    case 'COMPANY_CHALLENGES_ENDED':
      return t`Challenges ended`*/
    case 'COMPANY_CHALLENGES_PARTICIPANTS':
      return t`Challenge(s) participants`
    /*case 'COMPANY_USER_COMPANIES_CREATED':
      return t`User communities created`
    case 'COMPANY_USER_COMPANIES_REGISTERED_USERS':
      return t`Registered users for user communities`*/
    case 'COMPANY_MOST_ADVANCED_USERS':
      return t`Most advanced`
    case 'COMPANY_MOST_ENGAGED_USERS':
      return t`Most engaged`
    case 'COMPANY_MOST_ACTIVE_USERS':
      return t`Most active`
    default:
      return key
  }
}

const getStatIcon = (key: string) => {
  switch (key) {
    case 'COMPANY_EMPLOYEES':
      return <EmployeesIcon />
    case 'COMPANY_TOTAL_USERS':
    case 'COMPANY_CHALLENGES_PARTICIPANTS':
      return <TotalUsersIcon />
    case 'COMPANY_ACTIVE_USERS_FROM_TOTAL_PERCENTAGE':
      return <UsersEngagementRateIcon />
    case 'COMPANY_TOTAL_USERS_FROM_EMPLOYEES_PERCENTAGE':
      return <EmployeesEngagementRateIcon />
    case 'COMPANY_ACTIVE_USERS':
    case 'COMPANY_USER_COMPANIES_CREATED':
      return <ActiveUsersIcon />
    case 'COMPANY_REGISTERED_USERS':
    case 'COMPANY_USER_COMPANIES_REGISTERED_USERS':
      return <RegisteredUsersIcon />
    case 'COMPANY_HR_USERS_PERCENTAGE':
      return <HeartIcon />
    case 'COMPANY_TOTAL_MINUTES':
      return <HeartRateIcon />
    case 'COMPANY_MEDIAN_ACTIVITY':
      return <MedianActivityIcon />
    case 'COMPANY_EXEMPLARY_ACTIVE':
      return <ExemplaryActiveIcon />
    case 'COMPANY_MOST_ACTIVE_DAY':
      return <MostActiveDayIcon />
    case 'COMPANY_TOTAL_STEPS':
      return <TotalStepsIcon />
    case 'COMPANY_STEPS_PER_USER_PER_DAY':
      return <DailyStepsIcon />
    case 'COMPANY_TOTAL_CALORIES':
      return <TotalCaloriesIcon />
    case 'COMPANY_CHALLENGES_STARTED':
      return <ChallengesStartedIcon />
    case 'COMPANY_CHALLENGES':
    case 'COMPANY_CHALLENGES_ENDED':
      return <ChallengesEndedIcon />
    case 'COMPANY_MOST_ADVANCED_USERS':
      return <MostAdvancedIcon />
    case 'COMPANY_MOST_ENGAGED_USERS':
      return <MostEngagedIcon />
    case 'COMPANY_MOST_ACTIVE_USERS':
      return <MostActiveIcon />
    default:
      return ''
  }
}

const formatMostActiveDay = (startDate: string, result: number) => {
  let day = moment.utc(startDate).date(result)
  if (currentLang === Lang.EN) {
    day = day.locale('en')
  }
  return day.format('ddd, D MMM')
}

const formatStatResult = (stat: Stats) => {
  const { type, result, startDate } = stat
  switch (type) {
    //case 'COMPANY_CHALLENGES':
    //return 'TODO'
    case 'COMPANY_MOST_ADVANCED_USERS':
    case 'COMPANY_MOST_ENGAGED_USERS':
    case 'COMPANY_MOST_ACTIVE_USERS':
      return t`Top 10`
    case 'COMPANY_ACTIVE_USERS_FROM_TOTAL_PERCENTAGE':
    case 'COMPANY_TOTAL_USERS_FROM_EMPLOYEES_PERCENTAGE':
      return result === null ? 'N/A' : formatThousands(result) + '%'
    case 'COMPANY_HR_USERS_PERCENTAGE':
    case 'COMPANY_MEDIAN_ACTIVITY':
      return formatThousands(result) + '%'
    case 'COMPANY_EMPLOYEES':
      return result === null ? 'N/A' : formatThousands(result)
    case 'COMPANY_MOST_ACTIVE_DAY':
      return !result ? 'N/A' : formatMostActiveDay(startDate, result)
    default:
      return formatThousands(result)
  }
}

const getActiveDayDiff = (stats: StackedStats) => {
  const { current2, previous2 } = stats
  const change = (current2?.result || 0) - (previous2?.result || 0)
  const sign2 = change > 0 ? '+' : ''
  return sign2 + formatThousands(change)
}

const getChange = (stats: StackedStats) => {
  const { current, previous } = stats
  const { type, result } = current
  const { result: prevResult } = previous
  let change: any = result - prevResult
  const sign = change > 0 ? '+' : ''
  switch (type) {
    case 'COMPANY_MOST_ADVANCED_USERS':
    case 'COMPANY_MOST_ENGAGED_USERS':
    case 'COMPANY_MOST_ACTIVE_USERS':
      return ''
    // Append percentage.
    case 'COMPANY_ACTIVE_USERS_FROM_TOTAL_PERCENTAGE':
    case 'COMPANY_TOTAL_USERS_FROM_EMPLOYEES_PERCENTAGE':
    case 'COMPANY_HR_USERS_PERCENTAGE':
    case 'COMPANY_MEDIAN_ACTIVITY':
      change = formatThousands(change) + '%'
      break
    // Calculate percentage change.
    case 'COMPANY_ACTIVE_USERS':
    case 'COMPANY_EXEMPLARY_ACTIVE':
    case 'COMPANY_TOTAL_STEPS':
    case 'COMPANY_TOTAL_CALORIES':
    case 'COMPANY_TOTAL_MINUTES':
      change = !prevResult ? '100%' : formatThousands(Math.round(((change || 0) / (prevResult || 0)) * 100)) + '%'
      break
    case 'COMPANY_MOST_ACTIVE_DAY':
      return t`${getActiveDayDiff(stats)}% above median activity`
    default:
      change = formatThousands(change)
  }
  change = sign + change
  return t`${change} from last month`
}

const getBgColor = (type: string) => {
  switch (type) {
    case 'COMPANY_ACTIVE_USERS':
    case 'COMPANY_MEDIAN_ACTIVITY':
      return '#48B05A'
    /*case 'COMPANY_MOST_ADVANCED_USERS':
    case 'COMPANY_MOST_ENGAGED_USERS':
    case 'COMPANY_MOST_ACTIVE_USERS':
      return '#CCE2FF'*/
    default:
      return '#FFFFFF'
    //return '#F8F8F8'
  }
}

const getTextColor = (type: string) => {
  switch (type) {
    case 'COMPANY_ACTIVE_USERS':
    case 'COMPANY_MEDIAN_ACTIVITY':
      return '#FFFFFF'
    default:
      return 'inherit'
  }
}

const getBorderColor = (type: string) => {
  switch (type) {
    case 'COMPANY_ACTIVE_USERS':
    case 'COMPANY_MEDIAN_ACTIVITY':
      return '#48B05A'
    default:
      return '#CCCCCC'
  }
}

const getFooterOpacity = (type: string) => {
  switch (type) {
    case 'COMPANY_ACTIVE_USERS':
    case 'COMPANY_MEDIAN_ACTIVITY':
      return 1
    default:
      return 0.7
  }
}

const getTooltipText = (type: string) => {
  switch (type) {
    case 'COMPANY_ACTIVE_USERS_FROM_TOTAL_PERCENTAGE':
      return `${t`Total users`} / ${t`Active users`}`
    case 'COMPANY_TOTAL_USERS_FROM_EMPLOYEES_PERCENTAGE':
      return `${t`Employees`} / ${t`Total users`}`
    case 'COMPANY_EXEMPLARY_ACTIVE':
      return `100%+`
    case 'COMPANY_CHALLENGES':
      return t`Ongoing + ended`
    case 'COMPANY_MOST_ADVANCED_USERS':
      return t`Compared to last month`
    case 'COMPANY_MOST_ENGAGED_USERS':
      return t`Used app the most`
    case 'COMPANY_MOST_ACTIVE_USERS':
      return t`Highest median activity`
    default:
      return ''
  }
}

const isTop10 = (type: string) => {
  return ['COMPANY_MOST_ADVANCED_USERS', 'COMPANY_MOST_ENGAGED_USERS', 'COMPANY_MOST_ACTIVE_USERS'].includes(type)
}

const getTop10 = (data: string) => {
  if (!data) return 'N/A'
  const list = JSON.parse(data)
  return (
    <ol style={{ padding: '0 0 0 26px', margin: 0 }}>
      {[...Array(10)].map((n: number, i) => (
        <li key={i} style={{ opacity: list[i] ? 1 : 0.7 }}>
          {list[i] || '-'}
        </li>
      ))}
    </ol>
  )
}

export default function StatCard(props: IStatCardProps) {
  const { stats, editEmployees } = props
  if (!stats) return null
  const { current } = stats
  const { type } = current
  const tooltipText = getTooltipText(type)
  const change = getChange(stats)
  return (
    <div
      style={{
        background: getBgColor(type),
        color: getTextColor(type),
        borderRadius: '8px',
        border: `1px solid ${getBorderColor(type)}`,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 10px 10px 10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', lineHeight: '22px' }}>{getStatLabel(type)}</div>
          <div style={{ flexShrink: '0' }}>
            {editEmployees ? (
              <IconButton
                edge="end"
                onClick={editEmployees}
                style={{
                  width: '24px',
                  height: '24px',
                  padding: 0,
                  marginRight: '-4px',
                  marginTop: '-3px',
                }}
              >
                <EditIcon style={{ color: primaryBlue, width: 16, height: 16 }} />
              </IconButton>
            ) : tooltipText ? (
              <>
                <YumTooltip title={tooltipText} placement="top-end" arrow enterTouchDelay={0}>
                  <div style={{ display: 'inline-block' }}>
                    <InfoIcon />
                  </div>
                </YumTooltip>
              </>
            ) : (
              ''
            )}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyItems: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
          }}
        >
          <div style={{ width: '16px', marginRight: '8px', marginTop: '-3px' }}>{getStatIcon(type)}</div>
          {formatStatResult(current)}
        </div>
        {isTop10(type) && <div>{getTop10(current?.data)}</div>}
        {change && <div style={{ fontSize: '14px', opacity: getFooterOpacity(type) }}>{change}</div>}
      </div>
    </div>
  )
}
